"use client";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DownloadFolderDropdownMenuItem from "./downloadFolder";
import ExportFolderDropdownMenuItem from "./exportFolder";
import ExportFolderCaptionsDropdownMenuItem from "./exportFolderCaptions";
import { EllipsisVertical } from "lucide-react";
import { useStorageQuery } from "../../../../../hooks/storageQueryProvider";
import { GetFoldersResponseType } from "../../../../../hooks/useGetFolders";
import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function FolderOptions({
    folder,
    selected,
}: {
    folder: GetFoldersResponseType["data"][0];
    selected: boolean;
}) {
    const {
        setFolderSettingsModalOpen,
        setDeleteFolderModalOpen,
        setCreateYedaLmsCourseModalOpen,
        setSelectedFolders,
        selectedFolders,
        selectedFiles,
    } = useStorageQuery();

    const [open, setOpen] = useState(false);
    const { dir } = useLocale();

    const router = useRouter();

    const hasSelectedFilesOrFolders =
        selectedFolders.length > 0 || selectedFiles.length > 0;

    return (
        <div
            className={cn(
                "flex flex-row md:items-center gap-1.5 ps-2 pe-1",
                "opacity-0 group-hover:opacity-100 transition-all duration-200 focus-visible:ring-0",
                "bg-background group-hover:bg-accent",
                (open || selected) && "opacity-100"
            )}
        >
            <Checkbox
                className="bg-background"
                onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFolders((prev) => {
                        if (selected) {
                            return prev.filter((f) => f.id !== folder.id);
                        }

                        return [...prev, folder];
                    });
                }}
                checked={selected}
            />
            <DropdownMenu open={open} onOpenChange={setOpen} dir={dir}>
                <DropdownMenuTrigger asChild>
                    <button>
                        <EllipsisVertical className="size-3.5" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuLabel>
                        {t("storage.folders-grid.item.options.label")}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            const href = `/ai/upload-videos?folderId=${folder.id}`;
                            router.push(href);
                        }}
                    >
                        {t("storage.folders-grid.item.options.upload-videos", undefined, undefined)}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            setFolderSettingsModalOpen({
                                id: folder.id,
                                name: folder.name || "",
                                presetId: folder.presetId,
                            });
                        }}
                    >
                        {t("storage.folders-grid.item.options.edit", undefined, undefined)}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={hasSelectedFilesOrFolders}
                        onClick={(e) => {
                            e.stopPropagation();
                            setDeleteFolderModalOpen({
                                id: folder.id,
                                name: folder.name || "",
                            });
                        }}
                    >
                        {t("storage.folders-grid.item.options.delete", undefined, undefined)}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DownloadFolderDropdownMenuItem folderId={folder.id} />
                    <ExportFolderDropdownMenuItem folderId={folder.id} />
                    <ExportFolderCaptionsDropdownMenuItem
                        folderId={folder.id}
                    />
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            setCreateYedaLmsCourseModalOpen({
                                id: folder.id,
                                name: folder.name || "",
                            });
                        }}
                    >
                        {t(
                            "storage.folders-grid.item.options.create-yedalms-course"
                        )}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
